import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { useAuth } from '../contexts/AuthContext';
import UserSettings from './UserSettings';
import SyncTemplatesReadOnly from './SyncTemplatesReadOnly';
import SsoSettings from './SsoSettings';
import CredentialsTab from './CredentialsTab';
import ChainContactTab from './ChainContactTab';
import BillingTab from './BillingTab';
import accountService from '../services/accountService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import OhipTransactionsTab from './OhipTransactionsTab';
import SchedulingTab from './SchedulingTab';
import { SubTabs } from './shared/Tabs';
import './Settings.css';
import SupportTicketsPanel from './support/SupportTicketsPanel';
import SupportButton from './support/SupportButton';
import SupportDeskButton from './support/SupportDeskButton';
import { LifeBuoy, Bot, Bell } from 'lucide-react';
import ProductNotificationsTab from './shared/ProductNotificationsTab';
import McpChainSettings from './mcpservice/McpChainSettings';

const CREDENTIAL_SECTIONS = [{ key: 'owned', label: 'My Credentials' }, { key: 'shared', label: 'Shared With Me' }];

const Settings = () => {
  const { selectedAccount, isChainAdmin, accountData, setAccountData } = useAccount();
  const { isSuperAdmin, isPartner } = useAuth();
  // Partners managing their own chains have admin access. Treat them like chain admins for the gates.
  const hasChainAccess = isSuperAdmin || isChainAdmin || isPartner;
  // The Scheduling tab exists only for a chain with Enforce Scheduling on (V596).
  const schedulingOn = (accountData?.enforceScheduling || accountData?.account?.enforceScheduling) === 'Y';
  // The tab lives in ?tab= (and a grouped tab's section in ?sub=, as on the hotel screen) so it can be linked
  // to and survives a refresh. Replace, not push; an unknown value falls back to Details.
  //
  // Details groups Contact Information and Billing; Access groups Users and SSO. Each was its own tab, and
  // links still carry those names (the hotel Billing tab links to ?tab=billing), so LEGACY_TABS lands an old
  // name on its group with that section open.
  const TABS = ['details', 'access', 'subscriptions', 'templates', 'preferences', 'credentials', 'transactions', 'scheduling', 'mcp', 'notifications', 'support'];
  const SECTIONS = {
    details: [{ key: 'contact', label: 'Contact Information' }, { key: 'billing', label: 'Billing' }],
    access: [{ key: 'users', label: 'Users' }, { key: 'sso', label: 'SSO' }],
  };
  const LEGACY_TABS = { contact: 'details', billing: 'details', users: 'access', sso: 'access' };
  const firstSection = (tab) => (SECTIONS[tab] ? SECTIONS[tab][0].key : null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ activeTab, activeSection }, setTabState] = useState(() => {
    const t = searchParams.get('tab');
    if (LEGACY_TABS[t]) return { activeTab: LEGACY_TABS[t], activeSection: t };
    const tab = TABS.includes(t) ? t : 'details';
    const section = searchParams.get('sub');
    const valid = (SECTIONS[tab] || []).some(x => x.key === section);
    return { activeTab: tab, activeSection: valid ? section : firstSection(tab) };
  });
  const setActiveTab = (next, section = firstSection(next)) => {
    setTabState({ activeTab: next, activeSection: section });
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('tab', next);
      if (section) params.set('sub', section); else params.delete('sub');
      return params;
    }, { replace: true });
  };
  const [credentialsSubTab, setCredentialsSubTab] = useState('owned');
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefError, setPrefError] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const handleVaultEmailToggle = async (enabled) => {
    setPrefSaving(true);
    setPrefError(null);
    try {
      await accountService.patchAccount(selectedAccount, { vault_email_enabled: enabled ? 'Y' : 'N' });
      setAccountData(prev => ({ ...prev, vaultEmailEnabled: enabled ? 'Y' : 'N' }));
    } catch (err) {
      setPrefError('Failed to save preference');
    } finally {
      setPrefSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setPrefError('Please select an image file');
      return;
    }
    if (file.size > 500 * 1024) {
      setPrefError('Image must be under 500KB');
      return;
    }
    setLogoUploading(true);
    setPrefError(null);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result;
      try {
        await accountService.patchAccount(selectedAccount, { logo: base64, logo_content_type: file.type });
        setAccountData(prev => ({ ...prev, logo: base64, logoContentType: file.type }));
      } catch (err) {
        setPrefError('Failed to upload logo');
      } finally {
        setLogoUploading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoRemove = async () => {
    setLogoUploading(true);
    setPrefError(null);
    try {
      await accountService.patchAccount(selectedAccount, { logo: null, logo_content_type: null });
      setAccountData(prev => ({ ...prev, logo: null, logoContentType: null }));
    } catch (err) {
      setPrefError('Failed to remove logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const renderTabContent = () => {
    if (!selectedAccount) {
      return (
        <div className="tab-content disabled-state">
          <div className="no-tenant-message">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <h3>No Tenant Selected</h3>
            <p>Please select a tenant from the dropdown in the navigation bar to view and manage settings.</p>
          </div>
        </div>
      );
    }

    if (!hasChainAccess) {
      return (
        <div className="tab-content disabled-state">
          <div className="no-tenant-message">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
            <h3>Access Denied</h3>
            <p>You do not have admin access for this hotel chain.</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'details':
        return (
          <div className="tab-content">
            <SubTabs tabs={SECTIONS.details} active={activeSection} onChange={k => setActiveTab('details', k)} />
            {activeSection === 'billing'
              ? <BillingTab entity="chain" entityId={selectedAccount} />
              : <ChainContactTab accountId={selectedAccount} />}
          </div>
        );
      case 'access':
        return (
          // Not .tab-content: UserSettings has never sat inside it, and that class restyles every h3 and p.
          <div>
            <SubTabs tabs={SECTIONS.access} active={activeSection} onChange={k => setActiveTab('access', k)} />
            {activeSection === 'sso'
              ? <div className="tab-content"><SsoSettings accountId={selectedAccount} /></div>
              : <UserSettings />}
          </div>
        );
      case 'templates':
        return (
          <div className="tab-content">
            <SyncTemplatesReadOnly accountId={selectedAccount} />
          </div>
        );
      case 'subscriptions':
        return (
          <div className="tab-content">
            <p>Subscriptions coming soon.</p>
          </div>
        );
      case 'credentials':
        return (
          <div className="tab-content">
            <SubTabs tabs={CREDENTIAL_SECTIONS} active={credentialsSubTab} onChange={setCredentialsSubTab} />
            {credentialsSubTab === 'owned'
              ? <CredentialsTab entityType="account" entityId={selectedAccount} />
              : <SharedCredentialsView entityType="account" entityId={selectedAccount} />
            }
          </div>
        );
      case 'preferences':
        return (
          <div className="tab-content">
            <div className="preferences-section">
              <h2>Preferences</h2>
              <p className="preferences-subtitle">Configure behaviour for this account.</p>
              <div className="preference-row">
                <div className="preference-info">
                  <span className="preference-label">Email vault query results</span>
                  <span className="preference-description">Allow users to email vault search results directly from the results dialog.</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={accountData?.vaultEmailEnabled !== 'N'}
                    onChange={e => handleVaultEmailToggle(e.target.checked)}
                    disabled={prefSaving}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="logo-preference-wrapper">
                <div className="preference-row">
                  <div className="preference-info">
                    <span className="preference-label">Account Logo</span>
                    <span className="preference-description">Upload a logo for use in canonical templates. Max 500KB, PNG/JPG/SVG.</span>
                  </div>
                  <div className="logo-upload-controls">
                    <label className="btn-upload-logo">
                      {logoUploading ? 'Uploading...' : accountData?.logo ? 'Replace' : 'Upload'}
                      <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={logoUploading} style={{ display: 'none' }} />
                    </label>
                    {accountData?.logo && (
                      <button className="btn-remove-logo" onClick={handleLogoRemove} disabled={logoUploading}>Remove</button>
                    )}
                  </div>
                </div>
                {accountData?.logo && (
                  <div className="logo-preview-area">
                    <img src={accountData.logo} alt="Account logo" className="logo-preview-large" />
                  </div>
                )}
              </div>
              {prefError && <p className="preference-error">{prefError}</p>}
            </div>
          </div>
        );
      case 'transactions':
        return (
          <div className="tab-content">
            <OhipTransactionsTab entityType="chain" entityId={selectedAccount} />
          </div>
        );
      // Only for a chain with Enforce Scheduling on (Accounts › Account Settings, V596).
      case 'scheduling':
        return schedulingOn ? <SchedulingTab accountId={selectedAccount} /> : null;
      // MCP as a Service — the chain's own settings (moved off /mcp-service's Chain tab, 2026-10-19).
      case 'mcp':
        if (!isSuperAdmin) return null;
        return (
          <div className="tab-content">
            <McpChainSettings accountId={selectedAccount} />
          </div>
        );
      // Support tickets about the chain and every hotel in it (shared list — support/SupportTicketsPanel).
      case 'notifications':
        return (
          <div className="tab-content">
            <ProductNotificationsTab level="chain" id={selectedAccount} />
          </div>
        );
      case 'support':
        return (
          <div className="tab-content">
            <div className="support-panel-actions">
              <SupportButton context={{ product: 'Hub', screen: 'chain-settings', accountId: selectedAccount }} label="New ticket" variant="primary" />
            </div>
            <SupportTicketsPanel scope="account" accountId={selectedAccount} emptyText="No support tickets for this chain or its hotels yet" />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-header settings-header-actions">
        <div>
          <h1>Settings</h1>
          <p className="settings-subtitle">Manage your hotel chain, properties, and user access</p>
        </div>
        {/* The chain as an organization on the support desk — the partner's admins add it (support/SupportDeskButton). */}
        {selectedAccount && hasChainAccess && <SupportDeskButton entity="chain" id={selectedAccount} />}
      </div>

      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'details' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('details')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 10a3 3 0 100-6 3 3 0 000 6zM10 12c-3 0-6 1.5-6 4v1h12v-1c0-2.5-3-4-6-4z" fill="currentColor"/>
          </svg>
          <span className="tab-label">Details</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'access' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('access')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
          </svg>
          <span className="tab-label">Access</span>
        </button>

<button
          className={`tab-button ${activeTab === 'subscriptions' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('subscriptions')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z" fill="currentColor"/>
          </svg>
          <span className="tab-label">Subscriptions</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'templates' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('templates')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M6 7h8M6 10h8M6 13h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span className="tab-label">Sync Templates</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'preferences' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('preferences')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M17.5 11h-1.26a6.01 6.01 0 00-.64-1.54l.9-.9a.5.5 0 000-.7l-1.42-1.42a.5.5 0 00-.7 0l-.9.9A6.01 6.01 0 0012 6.76V5.5a.5.5 0 00-.5-.5h-2a.5.5 0 00-.5.5v1.26a6.01 6.01 0 00-1.54.64l-.9-.9a.5.5 0 00-.7 0L4.44 7.92a.5.5 0 000 .7l.9.9A6.01 6.01 0 004.7 11H3.5a.5.5 0 00-.5.5v2c0 .28.22.5.5.5h1.26c.16.55.38 1.07.64 1.54l-.9.9a.5.5 0 000 .7l1.42 1.42c.2.2.5.2.7 0l.9-.9c.47.26.99.48 1.54.64V19.5c0 .28.22.5.5.5h2c.28 0 .5-.22.5-.5v-1.26a6.01 6.01 0 001.54-.64l.9.9c.2.2.5.2.7 0l1.42-1.42a.5.5 0 000-.7l-.9-.9c.26-.47.48-.99.64-1.54h1.26c.28 0 .5-.22.5-.5v-2a.5.5 0 00-.5-.5zM10.5 14a2.5 2.5 0 110-5 2.5 2.5 0 010 5z" fill="currentColor"/>
          </svg>
          <span className="tab-label">Preferences</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'credentials' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('credentials')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <rect x="3" y="7" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M7 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="10" cy="12" r="1.5" fill="currentColor"/>
          </svg>
          <span className="tab-label">Credentials</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'transactions' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('transactions')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M3 5h14M3 10h14M3 15h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <circle cx="15" cy="15" r="3" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M15 13.5v1.5l1 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
          </svg>
          <span className="tab-label">Transactions</span>
        </button>

        {schedulingOn && (
          <button
            className={`tab-button ${activeTab === 'scheduling' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
            onClick={() => selectedAccount && hasChainAccess && setActiveTab('scheduling')}
            disabled={!selectedAccount || !hasChainAccess}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M3 8h14M7 2.5v3M13 2.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <rect x="6" y="11" width="5" height="3" rx="1" fill="currentColor"/>
            </svg>
            <span className="tab-label">Scheduling</span>
          </button>
        )}

        {/* MCP as a Service — superadmin only for now (HIL-140). */}
        {isSuperAdmin && (
        <button
          className={`tab-button sa-only ${activeTab === 'mcp' ? 'active' : ''} ${!selectedAccount ? 'disabled' : ''}`}
          onClick={() => selectedAccount && setActiveTab('mcp')}
          disabled={!selectedAccount}
        >
          <Bot size={20} />
          <span className="tab-label">MCP</span>
        </button>
        )}

        <button
          className={`tab-button ${activeTab === 'notifications' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('notifications')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <Bell size={20} />
          <span className="tab-label">Notifications</span>
        </button>

        <button
          className={`tab-button ${activeTab === 'support' ? 'active' : ''} ${!selectedAccount || !hasChainAccess ? 'disabled' : ''}`}
          onClick={() => selectedAccount && hasChainAccess && setActiveTab('support')}
          disabled={!selectedAccount || !hasChainAccess}
        >
          <LifeBuoy size={20} />
          <span className="tab-label">Support</span>
        </button>

      </div>

      <div className="settings-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

function SharedCredentialsView({ entityType, entityId }) {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [overrides, setOverrides] = useState({}); // { [marketplaceId]: { [settingName]: value } }
  const [saving, setSaving] = useState({});
  const [saveError, setSaveError] = useState({});

  useEffect(() => {
    if (entityId) fetchShared();
  }, [entityId]); // eslint-disable-line

  const fetchShared = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await entityMktSettingsService.getSharedWithEntity(entityType, entityId);
      const creds = res.credentials || [];
      setCredentials(creds);
      // Seed override inputs with existing saved override values
      const initial = {};
      creds.forEach(cred => {
        cred.settings.filter(s => s.must_be_overridden === 'Y').forEach(s => {
          if (!initial[cred.marketplace_id]) initial[cred.marketplace_id] = {};
          initial[cred.marketplace_id][s.setting_name] = s.override_value || '';
        });
      });
      setOverrides(initial);
    } catch (err) {
      setError('Failed to load shared credentials');
    } finally {
      setLoading(false);
    }
  };

  const setOverrideValue = (marketplaceId, settingName, value) => {
    setOverrides(prev => ({
      ...prev,
      [marketplaceId]: { ...(prev[marketplaceId] || {}), [settingName]: value }
    }));
  };

  const handleSaveOverrides = async (cred) => {
    const mktId = cred.marketplace_id;
    const vals = overrides[mktId] || {};
    const overrideSettings = cred.settings
      .filter(s => s.must_be_overridden === 'Y')
      .map(s => ({ setting_name: s.setting_name, value: vals[s.setting_name] || '' }));

    setSaving(prev => ({ ...prev, [mktId]: true }));
    setSaveError(prev => ({ ...prev, [mktId]: null }));
    try {
      await entityMktSettingsService.saveSharedOverrides(entityType, entityId, mktId, overrideSettings);
    } catch (err) {
      setSaveError(prev => ({ ...prev, [mktId]: 'Failed to save' }));
    } finally {
      setSaving(prev => ({ ...prev, [mktId]: false }));
    }
  };

  const filtered = credentials.filter(cred => {
    if (!search) return true;
    const s = search.toLowerCase();
    return cred.marketplace_name.toLowerCase().includes(s) || cred.source_name?.toLowerCase().includes(s);
  });

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>Shared With Me</h3>
        <p style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#64748b' }}>
          Credentials shared with this chain. Enter values for any required fields.
        </p>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by marketplace or source..."
          style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
        />
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#dc2626', borderRadius: '6px', fontSize: '13px', marginBottom: '1rem' }}>{error}</div>
      )}

      {loading ? (
        <div style={{ color: '#64748b', fontSize: '13px' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '13px', border: '1px dashed #e2e8f0', borderRadius: '8px' }}>
          {search ? 'No shared credentials match your search.' : 'No credentials have been shared with this chain.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map(cred => {
            const hasOverrides = cred.settings.some(s => s.must_be_overridden === 'Y');
            return (
              <div key={cred.marketplace_id} style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {cred.marketplace_image && (
                    <img src={cred.marketplace_image} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  )}
                  <span style={{ fontWeight: 600, fontSize: '14px', color: '#1e293b' }}>{cred.marketplace_name}</span>
                  <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: 'auto' }}>
                    Shared by <strong>{cred.source_name || cred.source_type}</strong>
                  </span>
                </div>
                <div style={{ padding: '12px 16px' }}>
                  {cred.settings.map(s => (
                    <div key={s.setting_name} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151', minWidth: '200px' }}>
                        {s.setting_name}
                        {s.must_be_overridden === 'Y' && <span style={{ color: '#dc2626', marginLeft: '3px' }}>*</span>}
                      </span>
                      {s.must_be_overridden === 'Y' ? (
                        <input
                          type={s.datatype === 'PASSWORD' ? 'password' : 'text'}
                          value={(overrides[cred.marketplace_id] || {})[s.setting_name] || ''}
                          onChange={e => setOverrideValue(cred.marketplace_id, s.setting_name, e.target.value)}
                          placeholder="Enter value..."
                          autoComplete={s.datatype === 'PASSWORD' ? 'new-password' : 'off'}
                          style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '13px' }}
                        />
                      ) : (
                        <span style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>Set by provider</span>
                      )}
                    </div>
                  ))}
                  {hasOverrides && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
                      <button
                        type="button"
                        onClick={() => handleSaveOverrides(cred)}
                        disabled={saving[cred.marketplace_id]}
                        style={{ padding: '7px 16px', background: '#1e40af', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', opacity: saving[cred.marketplace_id] ? 0.6 : 1 }}
                      >
                        {saving[cred.marketplace_id] ? 'Saving...' : 'Save'}
                      </button>
                      {saveError[cred.marketplace_id] && (
                        <span style={{ fontSize: '12px', color: '#dc2626' }}>{saveError[cred.marketplace_id]}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Settings;
